/**
 * Geo-Restriction Constants
 *
 * Client-side mirror of the backend geo-blocking tier system.
 * Used for UI enforcement (hiding/showing features based on geo tier).
 */

// =============================================================================
// TIER TYPES
// =============================================================================

export type GeoTier = 'full' | 'restricted' | 'minimum';

export type GeoFeature = 'nsfw_models' | 'group_chat' | 'explicit_content';

// =============================================================================
// TIER RESTRICTIONS (mirrors constants/geo/features.py)
// =============================================================================

/** Features blocked per tier. Full and restricted have no blocks. */
export const GEO_TIER_RESTRICTIONS: Record<GeoTier, Set<GeoFeature>> = {
  full: new Set(),
  restricted: new Set(),
  minimum: new Set(['nsfw_models', 'group_chat', 'explicit_content']),
};

// =============================================================================
// NSFW PROVIDERS (mirrors constants/geo/providers.py)
// =============================================================================

/** Image providers flagged as NSFW (no safety filter) */
export const NSFW_IMAGE_PROVIDERS = new Set(['fooocus', 'black-forest-labs-flux-1-dev']);

/** LLM models flagged as NSFW (unfiltered output) */
export const NSFW_LLM_MODELS = new Set(['mythomax-l2-13b', 'noromaid-20b', 'midnight-rose-70b']);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/** Check if a feature is allowed for a given tier */
export function isFeatureAllowedForTier(feature: GeoFeature, tier: GeoTier): boolean {
  const blocked = GEO_TIER_RESTRICTIONS[tier];
  return !blocked?.has(feature);
}

/** Check if an image provider is NSFW-flagged */
export function isNsfwImageProvider(provider: string): boolean {
  return NSFW_IMAGE_PROVIDERS.has(provider);
}

/** Check if an LLM model is NSFW-flagged */
export function isNsfwLlmModel(model: string): boolean {
  if (NSFW_LLM_MODELS.has(model)) return true;
  const modelLower = model.toLowerCase();
  for (const nsfwModel of NSFW_LLM_MODELS) {
    if (modelLower.includes(nsfwModel)) return true;
  }
  return false;
}
